// Shared helpers for the Product Studio editor (edit mode) and the create
// wizard. Anything here is used by both — do not add create-only or
// edit-only logic; keep those in their own modules.

export const getProductFamilyOptions = (t) => [
  { value: "shoes", label: t("catalogProductStudio.familyShoes") },
  { value: "textile", label: t("catalogProductStudio.familyTextile") },
  { value: "accessories", label: t("catalogProductStudio.familyAccessories") },
];

export const CATALOG_MEDIA_ROLE_VALUES = [
  "HERO",
  "GALLERY",
  "DETAIL",
  "TEXTURE",
  "MODEL",
  "PACKAGING",
  "LIFESTYLE",
  "OTHER",
];

export const getCatalogMediaRoles = (t) => [
  { value: "HERO", label: t("catalogProductStudio.mediaRoleHero") },
  { value: "GALLERY", label: t("catalogProductStudio.mediaRoleGallery") },
  { value: "DETAIL", label: t("catalogProductStudio.mediaRoleDetail") },
  { value: "TEXTURE", label: t("catalogProductStudio.mediaRoleTexture") },
  { value: "MODEL", label: t("catalogProductStudio.mediaRoleModel") },
  { value: "PACKAGING", label: t("catalogProductStudio.mediaRolePackaging") },
  { value: "LIFESTYLE", label: t("catalogProductStudio.mediaRoleLifestyle") },
  { value: "OTHER", label: t("catalogProductStudio.mediaRoleOther") },
];

export const normalizeMediaRole = (value) =>
  CATALOG_MEDIA_ROLE_VALUES.includes(value) ? value : "GALLERY";

export const createEmptyMediaItem = () => ({
  id: `${Date.now()}-${Math.round(Math.random() * 1e9)}`,
  storageKey: "",
  sourceUrl: "",
  originalName: "",
  altText: "",
  role: "GALLERY",
  variantIds: [],
  // Colours this image is the colour cover for. Empty is the normal state —
  // colour covers are optional and fall back to existing behaviour.
  colorCoverOptionValueIds: [],
  // Phase 2 variant keys — populated from the upload response when sharp processing
  // succeeds; empty string when absent (old images or failed processing).
  thumbnailKey: "",
  cardKey: "",
  fullKey: "",
});

export const createEmptyEnrichmentForm = () => ({
  productStory: "",
  material: "",
  composition: "",
  careInstructions: "",
  fitNotes: "",
  sizeGuideNotes: "",
  stylingNotes: "",
  highlightsText: "",
  badgesText: "",
  detailSections: [],
});

export const createEmptyDetailSection = () => ({
  title: "",
  body: "",
});

export const splitMultilineList = (value) =>
  String(value || "")
    .split(/\n+/)
    .map((entry) => entry.trim())
    .filter(Boolean);

export const joinMultilineList = (value) =>
  Array.isArray(value) ? value.filter(Boolean).join("\n") : "";

export const mapEnrichmentToForm = (enrichment = {}) => ({
  productStory: enrichment.story || "",
  material: enrichment.material || "",
  composition: enrichment.composition || "",
  careInstructions: enrichment.careInstructions || "",
  fitNotes: enrichment.fitNotes || "",
  sizeGuideNotes: enrichment.sizeGuideNotes || "",
  stylingNotes: enrichment.stylingNotes || "",
  highlightsText: joinMultilineList(enrichment.highlights),
  badgesText: joinMultilineList(enrichment.badges),
  detailSections: Array.isArray(enrichment.detailSections)
    ? enrichment.detailSections.map((section) => ({
        title: section.title || "",
        body: section.body || "",
      }))
    : [],
});

export const buildEnrichmentPayload = (form = createEmptyEnrichmentForm()) => ({
  productStory: form.productStory || "",
  material: form.material || "",
  composition: form.composition || "",
  careInstructions: form.careInstructions || "",
  fitNotes: form.fitNotes || "",
  sizeGuideNotes: form.sizeGuideNotes || "",
  stylingNotes: form.stylingNotes || "",
  highlights: splitMultilineList(form.highlightsText),
  badges: splitMultilineList(form.badgesText),
  detailSections: (form.detailSections || [])
    .map((section) => ({
      title: String(section.title || "").trim(),
      body: String(section.body || "").trim(),
    }))
    .filter((section) => section.title && section.body),
});

export const coerceNullableNumber = (value) => {
  if (value === "" || value === null || value === undefined) {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const toInputValue = (value) =>
  value === null || value === undefined || Number.isNaN(value) ? "" : String(value);

export const getReferenceOptionLabel = (record, fallback) =>
  String(
    record?.name ??
      record?.colorName ??
      record?.brandName ??
      record?.specificType ??
      record?.label ??
      fallback
  ).trim() || fallback;

export const moveListItem = (items, index, direction) => {
  const targetIndex = index + direction;
  if (targetIndex < 0 || targetIndex >= items.length) {
    return items;
  }

  const nextItems = [...items];
  const [movedItem] = nextItems.splice(index, 1);
  nextItems.splice(targetIndex, 0, movedItem);
  return nextItems;
};
